// Staff-side portal seam (lives on the Client screen). Uses the normal staff
// `apiFetch` (firm JWT), NOT the portal token.
import { apiFetch } from "@/lib/api";

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

export interface StaffPortalAccess {
  id: string;
  party: "T" | "S";
  email: string;
  isActive: boolean;
  showDocuments: boolean;
  showFinancials: boolean;
  lastLoginAt: string | null;
}

function mapAccess(a: Record<string, unknown>): StaffPortalAccess {
  return {
    id: a.id as string,
    party: a.party as "T" | "S",
    email: (a.email as string) ?? "",
    isActive: Boolean(a.is_active),
    showDocuments: Boolean(a.show_documents),
    showFinancials: Boolean(a.show_financials),
    lastLoginAt: (a.last_login_at as string) ?? null,
  };
}

export async function getPortalAccess(clientId: string): Promise<StaffPortalAccess[]> {
  const res = await apiFetch<Envelope<Record<string, unknown>[]>>(
    `/portal/staff/clients/${clientId}/access/`,
  );
  return res.data.map(mapAccess);
}

export async function togglePortalAccess(
  clientId: string,
  body: { party?: "T" | "S"; show_documents?: boolean; show_financials?: boolean; email?: string },
): Promise<StaffPortalAccess> {
  const res = await apiFetch<Envelope<Record<string, unknown>>>(
    `/portal/staff/clients/${clientId}/access/`,
    { method: "PATCH", json: body },
  );
  return mapAccess(res.data);
}

export async function sendFillRequest(clientId: string): Promise<{ detail: string }> {
  const res = await apiFetch<Envelope<{ detail: string }>>(
    `/portal/staff/clients/${clientId}/send-fill-request/`,
    { method: "POST" },
  );
  return res.data;
}

export async function sendPortalInvite(clientId: string): Promise<{ detail: string }> {
  const res = await apiFetch<Envelope<{ detail: string }>>(
    `/portal/staff/clients/${clientId}/send-portal-invite/`,
    { method: "POST" },
  );
  return res.data;
}

export type OnboardingStatus = "PENDING" | "COMPLETED" | "EXPIRED";

export interface OnboardingSession {
  id: string;
  client: string;
  clientName: string | null;
  status: OnboardingStatus;
  createdByName: string | null;
  expiresAt: string;
  completedAt: string | null;
  createdAt: string;
  isPending: boolean;
}

function mapOnboarding(a: Record<string, unknown>): OnboardingSession {
  return {
    id: a.id as string,
    client: a.client as string,
    clientName: (a.client_name as string) ?? null,
    status: a.status as OnboardingStatus,
    createdByName: (a.created_by_name as string) ?? null,
    expiresAt: a.expires_at as string,
    completedAt: (a.completed_at as string) ?? null,
    createdAt: a.created_at as string,
    isPending: Boolean(a.is_pending),
  };
}

/** GET /portal/staff/onboarding/ — the firm's onboarding sessions (Control
 * Center Onboarding tab). Defaults to pending; pass a status to filter. */
export async function listOnboardingSessions(
  status?: OnboardingStatus,
): Promise<OnboardingSession[]> {
  const q = new URLSearchParams({ page_size: "100" });
  if (status) q.set("status", status);
  const res = await apiFetch<Envelope<Record<string, unknown>[]>>(
    `/portal/staff/onboarding/?${q.toString()}`,
  );
  return res.data.map(mapOnboarding);
}
